import type {
  BundleManifest,
  BundleManifestV11,
  ParticipantDetailV1,
  ParticipantIndexV1,
  RecordingCapabilitiesV1,
  RecordingIndexV1,
  SignalOverviewV1,
  SignalProfileV1,
  SignalWindowV1,
} from "@rekonas/domain";
import { PORTABLE_RECORDING_REF_PATTERN } from "@rekonas/domain";

type JsonObject = Record<string, unknown>;

const WORKSPACE_PATTERN = /^(?:[a-z0-9]|[a-z0-9][a-z0-9-]{0,118}[a-z0-9])$/;
const RELEASE_PATTERN = /^release-[0-9]{3,}$/;
const SEMVER_PATTERN = /^[0-9]+\.[0-9]+\.[0-9]+(?:[-+][A-Za-z0-9.-]+)?$/;
const PARTICIPANT_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
const MODALITY_PATTERN = /^[a-z0-9][a-z0-9._-]{0,63}$/;
const CHANNEL_PATTERN = /^[a-z0-9][a-z0-9._-]{0,119}$/;
const SHA256_PATTERN = /^[0-9a-f]{64}$/;
const REASON_CODE_PATTERN = /^[A-Z0-9_]{1,80}$/;
const TIMEZONE_SUFFIX_PATTERN = /(?:Z|[+-]\d{2}:\d{2})$/;
const SAFE_PATH_COMPONENT_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

function fail(context: string): never {
  throw new TypeError(`Invalid ${context}`);
}

function object(value: unknown, context: string): JsonObject {
  if (typeof value !== "object" || value === null || Array.isArray(value)) fail(context);
  return value as JsonObject;
}

function exactKeys(
  value: JsonObject,
  required: readonly string[],
  optional: readonly string[],
  context: string,
): void {
  for (const key of required) if (!(key in value)) fail(`${context}.${key}`);
  const accepted = new Set([...required, ...optional]);
  for (const key of Object.keys(value)) if (!accepted.has(key)) fail(`${context}.${key}`);
}

function string(value: unknown, context: string, max = Number.POSITIVE_INFINITY): string {
  if (typeof value !== "string" || value.length === 0 || value.length > max) fail(context);
  return value;
}

function nullableString(value: unknown, context: string, max: number): string | null {
  if (value === null) return null;
  return string(value, context, max);
}

function finite(value: unknown, context: string, min?: number, max?: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) fail(context);
  if ((min !== undefined && value < min) || (max !== undefined && value > max)) fail(context);
  return value;
}

function integer(
  value: unknown,
  context: string,
  min = 0,
  max = Number.POSITIVE_INFINITY,
): number {
  const parsed = finite(value, context, min, max);
  if (!Number.isInteger(parsed)) fail(context);
  return parsed;
}

function boolean(value: unknown, context: string): boolean {
  if (typeof value !== "boolean") fail(context);
  return value;
}

function array(value: unknown, context: string, max = Number.POSITIVE_INFINITY): unknown[] {
  if (!Array.isArray(value) || value.length > max) fail(context);
  return value;
}

function literal<T extends string>(
  value: unknown,
  accepted: readonly T[],
  context: string,
): T {
  if (typeof value !== "string" || !accepted.includes(value as T)) fail(context);
  return value as T;
}

function patterned(value: unknown, pattern: RegExp, context: string): string {
  const parsed = string(value, context);
  if (!pattern.test(parsed)) fail(context);
  return parsed;
}

function stringArray(value: unknown, context: string, max: number): string[] {
  return array(value, context, max).map((item, index) =>
    string(item, `${context}[${index}]`),
  );
}

function assertUnique(values: string[], context: string): void {
  if (new Set(values).size !== values.length) fail(context);
}

function bundlePath(value: unknown, context: string): string {
  const path = string(value, context);
  const components = path.split("/");
  if (
    path.startsWith("/") ||
    path.includes("\\") ||
    components.length > 8 ||
    components.some((component) => !SAFE_PATH_COMPONENT_PATTERN.test(component))
  ) {
    fail(context);
  }
  return path;
}

export function parseBundleManifest(value: unknown): BundleManifest {
  const manifest = object(value, "bundle manifest");
  const version = literal(manifest.bundle_format_version, ["1.0", "1.1"], "bundle version");
  const required = [
    "bundle_format_version",
    "workspace",
    "release_id",
    "release_number",
    "dashboard_target",
    "dashboard_application_version",
    "created_at",
    "entrypoints",
    "files",
  ];
  exactKeys(
    manifest,
    version === "1.1" ? [...required, "sponsor_profile"] : required,
    ["producer"],
    "bundle manifest",
  );
  patterned(manifest.workspace, WORKSPACE_PATTERN, "manifest workspace");
  const releaseId = patterned(manifest.release_id, RELEASE_PATTERN, "manifest release_id");
  const releaseNumber = integer(manifest.release_number, "manifest release_number", 1);
  if (Number(releaseId.slice("release-".length)) !== releaseNumber) fail("release identity");
  literal(manifest.dashboard_target, ["sponsor-dashboard"], "dashboard target");
  patterned(manifest.dashboard_application_version, SEMVER_PATTERN, "application version");
  if (
    typeof manifest.created_at !== "string" ||
    !TIMEZONE_SUFFIX_PATTERN.test(manifest.created_at) ||
    !Number.isFinite(Date.parse(manifest.created_at))
  ) {
    fail("manifest created_at");
  }
  const entrypoints = object(manifest.entrypoints, "manifest entrypoints");
  const entrypointRequired = ["dashboard_data", "methods"];
  if (version === "1.1") {
    entrypointRequired.push("participants_index", "recordings_index", "signal_profile");
  }
  exactKeys(entrypoints, entrypointRequired, ["signal_index"], "manifest entrypoints");
  const requiredEntrypoints = entrypointRequired.map((key) =>
    bundlePath(entrypoints[key], `entrypoints.${key}`),
  );
  let signalIndex: string | null = null;
  if (entrypoints.signal_index !== undefined && entrypoints.signal_index !== null) {
    signalIndex = bundlePath(entrypoints.signal_index, "signal_index");
  }
  const files = array(manifest.files, "manifest files", 10_000);
  if (files.length < (version === "1.1" ? 5 : 2)) fail("manifest files");
  const paths = files.map((item, index) => {
    const file = object(item, `manifest files[${index}]`);
    exactKeys(file, ["path", "sha256", "size"], ["media_type"], "bundle file");
    const path = bundlePath(file.path, "bundle file path");
    patterned(file.sha256, SHA256_PATTERN, "bundle file sha256");
    integer(file.size, "bundle file size");
    if (file.media_type !== undefined && file.media_type !== null) {
      string(file.media_type, "bundle file media_type");
    }
    return path;
  });
  assertUnique(paths, "manifest file paths");
  for (const entrypoint of [...requiredEntrypoints, ...(signalIndex ? [signalIndex] : [])]) {
    if (!paths.includes(entrypoint)) fail("manifest entrypoint files");
  }
  if (manifest.producer !== undefined && manifest.producer !== null) {
    const producer = object(manifest.producer, "bundle producer");
    exactKeys(producer, ["name"], ["version", "checksum"], "bundle producer");
    string(producer.name, "bundle producer name", 200);
    for (const key of ["version", "checksum"] as const) {
      if (producer[key] !== undefined && producer[key] !== null) {
        string(producer[key], `bundle producer ${key}`, 200);
      }
    }
  }
  if (version === "1.1") {
    literal(manifest.sponsor_profile, ["participants-data/1.0"], "sponsor profile");
  }
  return manifest as unknown as BundleManifest;
}

export function parseParticipantIndex(value: unknown): ParticipantIndexV1 {
  const document = object(value, "participant index");
  exactKeys(
    document,
    ["version", "workspace", "release_id", "synthetic", "participants"],
    [],
    "participant index",
  );
  literal(document.version, ["1.0"], "participant index version");
  patterned(document.workspace, WORKSPACE_PATTERN, "participant index workspace");
  patterned(document.release_id, RELEASE_PATTERN, "participant index release");
  boolean(document.synthetic, "participant index synthetic");
  const references = array(document.participants, "participants", 100_000).map(
    (item, index) => {
      const participant = object(item, `participants[${index}]`);
      exactKeys(
        participant,
        [
          "participant_ref",
          "recording_count",
          "valid_nights",
        ],
        [
          "age_years",
          "sex",
          "phenotype_terms",
          "sequence",
          "signal_quality_pct",
          "response",
          "n3_swa_change_pct",
          "hypoxic_burden_pct_min_per_hour",
          "detail_asset",
        ],
        `participants[${index}]`,
      );
      const reference = patterned(
        participant.participant_ref,
        PARTICIPANT_PATTERN,
        "participant_ref",
      );
      if (participant.age_years !== undefined && participant.age_years !== null) {
        finite(participant.age_years, "age_years", 0, 120);
      }
      if (participant.sex !== undefined && participant.sex !== null) {
        literal(participant.sex, ["female", "male", "other", "unknown"], "sex");
      }
      const terms = stringArray(participant.phenotype_terms ?? [], "phenotype_terms", 20);
      if (terms.some((term) => term.length > 120)) fail("phenotype_terms");
      assertUnique(terms, "phenotype_terms");
      if (participant.sequence !== undefined) {
        nullableString(participant.sequence, "sequence", 80);
      }
      const recordings = integer(participant.recording_count, "recording_count");
      const validNights = integer(participant.valid_nights, "valid_nights");
      if (validNights > recordings) fail("valid_nights");
      for (const key of [
        "signal_quality_pct",
        "n3_swa_change_pct",
        "hypoxic_burden_pct_min_per_hour",
      ]) {
        if (participant[key] !== undefined && participant[key] !== null) {
          finite(participant[key], key, 0, 100);
        }
      }
      if (participant.response !== undefined && participant.response !== null) {
        literal(
          participant.response,
          ["responder", "non_responder", "unknown"],
          "response",
        );
      }
      if (participant.detail_asset !== undefined && participant.detail_asset !== null) {
        bundlePath(participant.detail_asset, "detail_asset");
      }
      return reference;
    },
  );
  assertUnique(references, "participant references");
  return document as unknown as ParticipantIndexV1;
}

export function parseParticipantDetail(value: unknown): ParticipantDetailV1 {
  const document = object(value, "participant detail");
  exactKeys(
    document,
    [
      "version",
      "workspace",
      "release_id",
      "participant_ref",
      "synthetic",
      "summary",
      "recording_refs",
      "methods_asset",
    ],
    [],
    "participant detail",
  );
  literal(document.version, ["1.0"], "participant detail version");
  patterned(document.workspace, WORKSPACE_PATTERN, "participant detail workspace");
  patterned(document.release_id, RELEASE_PATTERN, "participant detail release");
  patterned(document.participant_ref, PARTICIPANT_PATTERN, "participant_ref");
  boolean(document.synthetic, "participant detail synthetic");
  const summary = object(document.summary, "participant detail summary");
  exactKeys(summary, [], ["bmi_kg_m2", "clinical_context", "narrative"], "detail summary");
  if (summary.bmi_kg_m2 !== undefined && summary.bmi_kg_m2 !== null) {
    finite(summary.bmi_kg_m2, "bmi_kg_m2", Number.MIN_VALUE, 150);
  }
  for (const key of ["clinical_context", "narrative"]) {
    if (summary[key] !== undefined) {
      const text = nullableString(summary[key], key, 4000);
      if (text?.includes("<") || text?.includes(">")) fail(key);
    }
  }
  const references = stringArray(document.recording_refs, "recording_refs", 256);
  for (const reference of references) {
    if (!PORTABLE_RECORDING_REF_PATTERN.test(reference)) fail("recording_ref");
  }
  assertUnique(references, "recording_refs");
  bundlePath(document.methods_asset, "methods_asset");
  return document as unknown as ParticipantDetailV1;
}

export function parseRecordingIndex(value: unknown): RecordingIndexV1 {
  const document = object(value, "recordings index");
  exactKeys(
    document,
    ["version", "workspace", "release_id", "synthetic", "recordings"],
    [],
    "recordings index",
  );
  literal(document.version, ["1.0"], "recordings index version");
  patterned(document.workspace, WORKSPACE_PATTERN, "recordings index workspace");
  patterned(document.release_id, RELEASE_PATTERN, "recordings index release");
  boolean(document.synthetic, "recordings index synthetic");
  const references = array(document.recordings, "recordings", 100_000).map(
    (item, index) => {
      const recording = object(item, `recordings[${index}]`);
      exactKeys(
        recording,
        [
          "recording_ref",
          "participant_ref",
          "night_label",
          "duration_seconds",
          "signal_profile_id",
          "signal_profile_version",
          "synthetic",
        ],
        ["condition", "expected_modalities"],
        `recordings[${index}]`,
      );
      const reference = patterned(
        recording.recording_ref,
        PORTABLE_RECORDING_REF_PATTERN,
        "recording_ref",
      );
      patterned(recording.participant_ref, PARTICIPANT_PATTERN, "participant_ref");
      string(recording.night_label, "night_label", 240);
      if (recording.condition !== undefined) {
        nullableString(recording.condition, "condition", 240);
      }
      finite(recording.duration_seconds, "duration_seconds", Number.MIN_VALUE, 604_800);
      patterned(recording.signal_profile_id, MODALITY_PATTERN, "signal_profile_id");
      patterned(recording.signal_profile_version, SEMVER_PATTERN, "signal_profile_version");
      const modalities = stringArray(
        recording.expected_modalities ?? [],
        "expected_modalities",
        32,
      );
      modalities.forEach((id) => {
        if (!MODALITY_PATTERN.test(id)) fail("expected modality");
      });
      assertUnique(modalities, "expected modalities");
      boolean(recording.synthetic, "recording synthetic");
      return reference;
    },
  );
  assertUnique(references, "recording references");
  return document as unknown as RecordingIndexV1;
}

function validateAliases(value: unknown, context: string): void {
  const aliases = stringArray(value, context, 64);
  if (aliases.length === 0) fail(context);
  const normalized = aliases.map((alias) =>
    alias.trim().toLowerCase().replaceAll("μ", "µ"),
  );
  assertUnique(normalized, context);
}

function validateModality(value: unknown, index: number): string {
  const modality = object(value, `modalities[${index}]`);
  const kind = literal(
    modality.kind,
    ["scalar", "multichannel", "categorical", "quaternion", "media", "annotation", "unavailable"],
    "modality kind",
  );
  const common = ["id", "label", "kind", "required", "origin", "order", "fallback"];
  const perKind = {
    scalar: ["unit", "aliases", "sampling", "selection", "viewer", "overview"],
    multichannel: ["unit", "aliases", "sampling", "selection", "viewer", "overview"],
    categorical: ["aliases", "sampling", "selection", "viewer", "overview", "labels"],
    quaternion: ["unit", "components", "sampling", "viewer", "overview", "quaternion"],
    media: ["sampling", "viewer"],
    annotation: ["namespace", "labels", "epoch_seconds", "start_seconds", "viewer", "overview"],
    unavailable: ["state", "reason_code"],
  } as const;
  exactKeys(modality, [...common, ...perKind[kind]], [], `modalities[${index}]`);
  const id = patterned(modality.id, MODALITY_PATTERN, "modality id");
  string(modality.label, "modality label", 240);
  boolean(modality.required, "modality required");
  literal(modality.origin, ["recorded", "derived"], "modality origin");
  integer(modality.order, "modality order", 0, 10_000);
  literal(modality.fallback, ["show_status", "omit"], "modality fallback");
  if (kind === "scalar" || kind === "multichannel") {
    string(modality.unit, "modality unit", 80);
    validateAliases(modality.aliases, "modality aliases");
    literal(modality.sampling, ["native"], "modality sampling");
    literal(
      modality.selection,
      kind === "scalar" ? ["unique_match"] : ["all_matches", "unique_match"],
      "modality selection",
    );
    literal(modality.viewer, ["line", "waveform"], "modality viewer");
    literal(modality.overview, ["minmax"], "modality overview");
  } else if (kind === "categorical") {
    validateAliases(modality.aliases, "categorical aliases");
    literal(modality.sampling, ["native"], "categorical sampling");
    literal(modality.selection, ["unique_match"], "categorical selection");
    literal(modality.viewer, ["stages"], "categorical viewer");
    literal(modality.overview, ["intervals"], "categorical overview");
    const labels = object(modality.labels, "categorical labels");
    if (Object.keys(labels).length === 0 || Object.keys(labels).length > 64) fail("labels");
    Object.entries(labels).forEach(([code, label]) => {
      string(code, "categorical code", 64);
      string(label, "categorical label", 120);
    });
  } else if (kind === "quaternion") {
    literal(modality.unit, ["1"], "quaternion unit");
    const components = object(modality.components, "quaternion components");
    exactKeys(components, ["w", "x", "y", "z"], [], "quaternion components");
    const aliases: string[] = [];
    for (const component of ["w", "x", "y", "z"]) {
      validateAliases(components[component], `quaternion ${component}`);
      aliases.push(
        ...(components[component] as string[]).map((item) =>
          item.trim().toLowerCase().replaceAll("μ", "µ"),
        ),
      );
    }
    assertUnique(aliases, "quaternion component aliases");
    literal(modality.sampling, ["native_shared_clock"], "quaternion sampling");
    literal(modality.viewer, ["orientation3d"], "quaternion viewer");
    literal(modality.overview, ["none"], "quaternion overview");
    const convention = object(modality.quaternion, "quaternion convention");
    exactKeys(
      convention,
      [
        "order",
        "rotation",
        "handedness",
        "world_axes",
        "body_axes",
        "source_to_world_wxyz",
        "model_to_body_wxyz",
        "max_gap_seconds",
      ],
      [],
      "quaternion convention",
    );
    literal(convention.order, ["wxyz"], "quaternion order");
    literal(convention.rotation, ["active_body_to_world"], "quaternion rotation");
    literal(convention.handedness, ["right"], "quaternion handedness");
    string(convention.world_axes, "world_axes", 200);
    string(convention.body_axes, "body_axes", 200);
    for (const key of ["source_to_world_wxyz", "model_to_body_wxyz"]) {
      const quaternion = array(convention[key], key, 4).map((item) => finite(item, key));
      if (quaternion.length !== 4) fail(key);
      const norm = quaternion.reduce((sum, item) => sum + item ** 2, 0);
      if (norm < 0.81 || norm > 1.21) fail(key);
    }
    finite(convention.max_gap_seconds, "max_gap_seconds", Number.MIN_VALUE, 60);
  } else if (kind === "media") {
    literal(modality.sampling, ["media_clock"], "media sampling");
    literal(modality.viewer, ["audio"], "media viewer");
  } else if (kind === "annotation") {
    patterned(modality.namespace, /^[a-z][a-z0-9-]{0,63}\/$/, "annotation namespace");
    const labels = object(modality.labels, "annotation labels");
    if (Object.keys(labels).length === 0 || Object.keys(labels).length > 64) fail("annotation labels");
    Object.entries(labels).forEach(([code, label]) => {
      if (!code || code.includes("/") || code.length > 64) fail("annotation code");
      string(label, "annotation label", 120);
    });
    finite(modality.epoch_seconds, "annotation epoch", Number.MIN_VALUE, 3600);
    finite(modality.start_seconds, "annotation start", 0);
    literal(modality.viewer, ["stages"], "annotation viewer");
    literal(modality.overview, ["intervals"], "annotation overview");
    if (modality.origin !== "derived") fail("annotation origin");
  } else {
    literal(modality.state, ["not_recorded", "not_computed", "unsupported_mapping"], "unavailable state");
    patterned(modality.reason_code, REASON_CODE_PATTERN, "unavailable reason");
  }
  return id;
}

export function parseSignalProfile(value: unknown): SignalProfileV1 {
  const document = object(value, "signal profile");
  exactKeys(
    document,
    ["version", "profile_id", "profile_version", "time_basis", "modalities"],
    [],
    "signal profile",
  );
  literal(document.version, ["1.0", "1.1"], "signal profile version");
  patterned(document.profile_id, MODALITY_PATTERN, "profile id");
  patterned(document.profile_version, SEMVER_PATTERN, "profile version");
  literal(
    document.time_basis,
    ["seconds_from_recording_start"],
    "profile time basis",
  );
  const modalities = array(document.modalities, "modalities", 32);
  if (modalities.length === 0) fail("modalities");
  const ids = modalities.map((modality, index) => validateModality(modality, index));
  assertUnique(ids, "modality ids");
  const orders = modalities.map((raw) => object(raw, "modality").order as number);
  assertUnique(orders.map(String), "modality orders");
  return document as unknown as SignalProfileV1;
}

export function parseRecordingCapabilities(value: unknown): RecordingCapabilitiesV1 {
  const document = object(value, "recording capabilities");
  exactKeys(
    document,
    [
      "version",
      "recording_ref",
      "synthetic",
      "duration_seconds",
      "profile_id",
      "profile_version",
      "profile_sha256",
      "data_version",
      "time_origin",
      "modalities",
    ],
    [],
    "recording capabilities",
  );
  literal(document.version, ["1.0", "1.1"], "capabilities version");
  patterned(document.recording_ref, PORTABLE_RECORDING_REF_PATTERN, "recording_ref");
  boolean(document.synthetic, "capabilities synthetic");
  finite(document.duration_seconds, "duration_seconds", Number.MIN_VALUE, 604_800);
  patterned(document.profile_id, MODALITY_PATTERN, "profile id");
  patterned(document.profile_version, SEMVER_PATTERN, "profile version");
  patterned(document.profile_sha256, SHA256_PATTERN, "profile sha256");
  string(document.data_version, "data version", 120);
  if (document.time_origin !== null) string(document.time_origin, "time origin", 200);
  const rawModalities = array(document.modalities, "capability modalities", 32);
  if (rawModalities.length === 0) fail("capability modalities");
  const ids = rawModalities.map(
    (item, index) => {
      const modality = object(item, `capability modalities[${index}]`);
      exactKeys(
        modality,
        ["id", "required", "state", "channels"],
        ["reason_code", "retry_after_seconds", "overview", "annotation"],
        "modality capability",
      );
      const id = patterned(modality.id, MODALITY_PATTERN, "capability modality id");
      boolean(modality.required, "capability required");
      const state = literal(
        modality.state,
        ["available", "not_recorded", "not_computed", "temporarily_unavailable", "unsupported_mapping", "queued", "processing", "failed"],
        "capability state",
      );
      const channels = array(modality.channels, "capability channels", 256);
      channels.forEach((raw, channelIndex) => {
        const channel = object(raw, `capability channels[${channelIndex}]`);
        exactKeys(
          channel,
          [
            "id",
            "label",
            "unit",
            "sample_rate_hz",
            "start_seconds",
            "sample_count",
            "unit_conversion",
          ],
          [],
          "capability channel",
        );
        patterned(channel.id, CHANNEL_PATTERN, "channel id");
        string(channel.label, "channel label", 240);
        string(channel.unit, "channel unit", 80);
        finite(channel.sample_rate_hz, "sample rate", Number.MIN_VALUE);
        finite(channel.start_seconds, "channel start", 0);
        integer(channel.sample_count, "sample count");
        literal(
          channel.unit_conversion,
          [
            "identity",
            "v_to_uv",
            "mv_to_uv",
            "uv_to_v",
            "uv_to_mv",
            "v_to_mv",
            "mv_to_v",
          ],
          "unit conversion",
        );
      });
      let annotationAvailable = false;
      if (modality.annotation !== undefined && modality.annotation !== null) {
        const annotation = object(modality.annotation, "capability annotation");
        exactKeys(annotation, ["namespace", "labels", "epoch_seconds", "start_seconds", "provenance"], [], "capability annotation");
        patterned(annotation.namespace, /^[a-z][a-z0-9-]{0,63}\/$/, "capability annotation namespace");
        const labels = object(annotation.labels, "capability annotation labels");
        if (Object.keys(labels).length === 0 || Object.keys(labels).length > 64) fail("capability annotation labels");
        Object.values(labels).forEach((label) => string(label, "capability annotation label", 120));
        finite(annotation.epoch_seconds, "capability annotation epoch", Number.MIN_VALUE);
        finite(annotation.start_seconds, "capability annotation start", 0);
        object(annotation.provenance, "capability annotation provenance");
        annotationAvailable = true;
      }
      if ((state === "available") !== (channels.length > 0 || annotationAvailable)) fail("capability payload/state");
      if (channels.length && annotationAvailable) fail("capability selector ambiguity");
      const channelIds = channels.map((raw) => object(raw, "capability channel").id as string);
      assertUnique(channelIds, "capability channel ids");
      if (modality.reason_code !== undefined && modality.reason_code !== null) {
        patterned(modality.reason_code, REASON_CODE_PATTERN, "reason code");
      }
      if (
        modality.retry_after_seconds !== undefined &&
        modality.retry_after_seconds !== null
      ) {
        integer(modality.retry_after_seconds, "retry_after_seconds", 1, 86_400);
        if (state !== "temporarily_unavailable") fail("retry_after_seconds state");
      }
      if (modality.overview !== undefined && modality.overview !== null) {
        literal(
          modality.overview,
          [
            "available",
            "not_recorded",
            "not_computed",
            "temporarily_unavailable",
            "unsupported",
            "not_applicable",
          ],
          "overview state",
        );
      }
      return id;
    },
  );
  assertUnique(ids, "capability modality ids");
  return document as unknown as RecordingCapabilitiesV1;
}

function validateSignalInterval(value: unknown, context: string): JsonObject {
  const interval = object(value, context);
  exactKeys(interval, ["start_seconds", "end_seconds"], [], context);
  validateSignalIntervalBounds(interval, context);
  return interval;
}

function validateSignalIntervalBounds(interval: JsonObject, context: string): void {
  const start = finite(interval.start_seconds, `${context}.start_seconds`, 0);
  const end = finite(interval.end_seconds, `${context}.end_seconds`, Number.MIN_VALUE);
  if (end <= start) fail(context);
}

function validateSignalMetrics(value: unknown): void {
  const metrics = object(value, "signal metrics");
  exactKeys(
    metrics,
    [
      "storage_requests",
      "transferred_bytes",
      "decoded_bytes",
      "storage_ms",
      "decode_ms",
      "latency_ms",
      "cache_state",
    ],
    [],
    "signal metrics",
  );
  integer(metrics.storage_requests, "storage_requests");
  integer(metrics.transferred_bytes, "transferred_bytes");
  integer(metrics.decoded_bytes, "decoded_bytes");
  finite(metrics.storage_ms, "storage_ms", 0);
  finite(metrics.decode_ms, "decode_ms", 0);
  finite(metrics.latency_ms, "latency_ms", 0);
  literal(metrics.cache_state, ["hit", "generated", "not_applicable"], "cache_state");
}

function validateModalityStates(value: unknown): void {
  parseRecordingCapabilities({
    version: "1.0",
    recording_ref: "rec_00000000-0000-4000-8000-000000000000",
    synthetic: true,
    duration_seconds: 1,
    profile_id: "validation-profile",
    profile_version: "1.0.0",
    profile_sha256: "0".repeat(64),
    data_version: "validation",
    time_origin: null,
    modalities: value,
  });
}

function validateSignalSeries(value: unknown, context: string): string {
  const series = object(value, context);
  const representation = literal(
    series.representation,
    ["native", "minmax", "intervals"],
    `${context}.representation`,
  );
  const base = [
    "modality_id",
    "modality_label",
    "channel_id",
    "channel_label",
    "unit",
    "origin",
    "representation",
  ];
  const extra = representation === "native"
    ? ["start_seconds", "sample_period_seconds", "sample_count", "values"]
    : representation === "minmax"
      ? ["bins"]
      : ["labels", "intervals"];
  exactKeys(series, [...base, ...extra], [], context);
  patterned(series.modality_id, MODALITY_PATTERN, `${context}.modality_id`);
  string(series.modality_label, `${context}.modality_label`, 240);
  patterned(series.channel_id, CHANNEL_PATTERN, `${context}.channel_id`);
  string(series.channel_label, `${context}.channel_label`, 240);
  string(series.unit, `${context}.unit`, 80);
  literal(series.origin, ["recorded", "derived"], `${context}.origin`);
  if (representation === "native") {
    finite(series.start_seconds, `${context}.start_seconds`, 0);
    finite(series.sample_period_seconds, `${context}.sample_period_seconds`, Number.MIN_VALUE);
    const count = integer(series.sample_count, `${context}.sample_count`);
    const values = array(series.values, `${context}.values`, 200_000);
    if (values.length !== count) fail(`${context}.sample_count`);
    values.forEach((item, index) => {
      if (item !== null) finite(item, `${context}.values[${index}]`);
    });
  } else if (representation === "minmax") {
    array(series.bins, `${context}.bins`, 20_000).forEach((item, index) => {
      const bin = object(item, `${context}.bins[${index}]`);
      exactKeys(
        bin,
        ["start_seconds", "end_seconds", "minimum", "maximum", "valid_count"],
        [],
        `${context}.bins[${index}]`,
      );
      validateSignalIntervalBounds(bin, `${context}.bins[${index}]`);
      if (bin.minimum !== null) finite(bin.minimum, `${context}.minimum`);
      if (bin.maximum !== null) finite(bin.maximum, `${context}.maximum`);
      integer(bin.valid_count, `${context}.valid_count`);
      if ((bin.minimum === null) !== (bin.maximum === null)) fail(`${context}.bin bounds`);
      if (typeof bin.minimum === "number" && typeof bin.maximum === "number" && bin.minimum > bin.maximum) {
        fail(`${context}.bin bounds`);
      }
    });
  } else {
    const labels = object(series.labels, `${context}.labels`);
    if (Object.keys(labels).length === 0 || Object.keys(labels).length > 64) fail(`${context}.labels`);
    Object.entries(labels).forEach(([code, label]) => {
      if (!code || code.length > 64) fail(`${context}.labels`);
      string(label, `${context}.labels.${code}`, 120);
    });
    array(series.intervals, `${context}.intervals`, 20_000).forEach((item, index) => {
      const interval = object(item, `${context}.intervals[${index}]`);
      exactKeys(
        interval,
        ["start_seconds", "end_seconds", "code"],
        [],
        `${context}.intervals[${index}]`,
      );
      validateSignalIntervalBounds(interval, `${context}.intervals[${index}]`);
      const code = string(interval.code, `${context}.code`, 64);
      if (!(code in labels)) fail(`${context}.code`);
    });
  }
  return `${series.modality_id}:${series.channel_id}`;
}

export function parseSignalOverview(value: unknown): SignalOverviewV1 {
  const document = object(value, "signal overview");
  exactKeys(
    document,
    [
      "version",
      "recording_ref",
      "profile_version",
      "data_version",
      "synthetic",
      "interval",
      "series",
      "modality_states",
      "metrics",
      "overview_algorithm",
      "request_id",
    ],
    ["processing_version", "profile_sha256"],
    "signal overview",
  );
  literal(document.version, ["1.0", "1.1"], "overview version");
  patterned(document.recording_ref, PORTABLE_RECORDING_REF_PATTERN, "recording_ref");
  patterned(document.profile_version, SEMVER_PATTERN, "profile version");
  string(document.data_version, "data version", 120);
  boolean(document.synthetic, "overview synthetic");
  validateSignalInterval(document.interval, "overview interval");
  const overviewSeries = array(document.series, "overview series", 256);
  const identities = overviewSeries.map((item, index) => {
    const identity = validateSignalSeries(item, `overview series[${index}]`);
    if (object(item, "overview series").representation === "native") fail("overview representation");
    return identity;
  });
  assertUnique(identities, "overview series identities");
  const overviewValues = overviewSeries.reduce<number>((total, item) => {
    const series = object(item, "overview series");
    return total + (series.representation === "minmax"
      ? array(series.bins, "overview bins").length * 2
      : array(series.intervals, "overview intervals").length);
  }, 0);
  if (overviewValues > 20_000) fail("overview total values");
  validateModalityStates(document.modality_states);
  validateSignalMetrics(document.metrics);
  string(document.overview_algorithm, "overview algorithm", 120);
  string(document.request_id, "request id", 64);
  return document as unknown as SignalOverviewV1;
}

export function parseSignalWindow(value: unknown): SignalWindowV1 {
  const document = object(value, "signal window");
  exactKeys(
    document,
    [
      "version",
      "recording_ref",
      "profile_version",
      "data_version",
      "synthetic",
      "requested",
      "interval",
      "series",
      "modality_states",
      "metrics",
      "request_id",
    ],
    ["processing_version", "profile_sha256"],
    "signal window",
  );
  literal(document.version, ["1.0", "1.1"], "window version");
  patterned(document.recording_ref, PORTABLE_RECORDING_REF_PATTERN, "recording_ref");
  patterned(document.profile_version, SEMVER_PATTERN, "profile version");
  string(document.data_version, "data version", 120);
  boolean(document.synthetic, "window synthetic");
  const requested = object(document.requested, "window requested");
  exactKeys(requested, ["start_seconds", "duration_seconds"], [], "window requested");
  finite(requested.start_seconds, "requested start", 0);
  const requestedDuration = integer(requested.duration_seconds, "requested duration", 15, 60);
  if (![15, 30, 60].includes(requestedDuration)) fail("requested duration");
  validateSignalInterval(document.interval, "window interval");
  const windowSeries = array(document.series, "window series", 256);
  const identities = windowSeries.map((item, index) => {
    const identity = validateSignalSeries(item, `window series[${index}]`);
    if (object(item, "window series").representation === "minmax") fail("window representation");
    return identity;
  });
  assertUnique(identities, "window series identities");
  const windowValues = windowSeries.reduce<number>((total, item) => {
    const series = object(item, "window series");
    return total + (series.representation === "native"
      ? array(series.values, "window values").length
      : array(series.intervals, "window intervals").length);
  }, 0);
  if (windowValues > 200_000) fail("window total values");
  validateModalityStates(document.modality_states);
  validateSignalMetrics(document.metrics);
  string(document.request_id, "request id", 64);
  return document as unknown as SignalWindowV1;
}

export function isBundleManifestV11(manifest: BundleManifest): manifest is BundleManifestV11 {
  return manifest.bundle_format_version === "1.1";
}
